import React, { useState, useEffect } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  List,
  ListItem,
  Spinner,
  useColorModeValue,
  Link,
  Badge,
} from '@chakra-ui/react';
import Breadcrumbs from './shared/Breadcrumbs';
import fetchWithAuth from '../utils/fetchWithAuth';

function ResultsPage() {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const bgColor = useColorModeValue('background.shade1');
  const color = useColorModeValue('theme.black');
  const linkColor = useColorModeValue('theme.teal');
  const pollBoxBgColor = useColorModeValue('background.shade2');
  const headerBoxBgColor = useColorModeValue('background.shade2');

  useEffect(() => {
    // Fetch all polls (we'll show results for any that have responses)
    fetchWithAuth('/api/polls/')
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        setPolls(data.results || []);
        setLoading(false);
      })
      .catch(error => {
        setError(error);
        setLoading(false);
      });
  }, []);

  const getStateBadgeColor = (state) => {
    switch (state) {
      case 'draft':
        return 'gray';
      case 'staged':
        return 'blue';
      case 'live':
        return 'green';
      case 'closed':
        return 'red';
      default:
        return 'gray';
    }
  };

  if (loading) {
    return (
      <Box bg={bgColor} color={color} minHeight="100vh">
        <Box maxWidth="800px" margin="0 auto" px={4} py={8}>
          <Breadcrumbs
            items={[
              { label: 'Home', path: '/' },
              { label: 'Results' }
            ]}
          />
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Spinner size="xl" />
          </Box>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box bg={bgColor} color={color} minHeight="100vh">
        <Box maxWidth="800px" margin="0 auto" px={4} py={8}>
          <Breadcrumbs
            items={[
              { label: 'Home', path: '/' },
              { label: 'Results' }
            ]}
          />
          <Box textAlign="center" color="red.500" mt={8}>
            <Text>Error: {error.message}</Text>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box bg={bgColor} color={color} minHeight="100vh">
      <Box maxWidth="800px" margin="0 auto" px={4} py={8}>
        <Breadcrumbs
          items={[
            { label: 'Home', path: '/' },
            { label: 'Results' }
          ]}
        />
        <Box
          bg={headerBoxBgColor}
          p={6}
          borderRadius="md"
          border="1px solid"
          borderColor="border.default"
          mb={6}
        >
          <Heading as="h1" size="xl" textAlign="center">
            Poll Results
          </Heading>
          <Text textAlign="center" mt={2} color="theme.mediumGray">
            View responses and results for all polls
          </Text>
        </Box>

        {polls.length === 0 ? (
          <Box bg={pollBoxBgColor} p={8} borderRadius="md" border="1px solid" borderColor="border.default" textAlign="center">
            <Text>No polls available.</Text>
          </Box>
        ) : (
          <List spacing={4}>
            {polls.map(poll => (
              <ListItem
                key={poll.id}
                bg={pollBoxBgColor}
                p={6}
                borderRadius="md"
                border="1px solid"
                borderColor="border.default"
              >
                <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                  <Heading as="h3" size="md">
                    {poll.title}
                  </Heading>
                  <Badge colorScheme={getStateBadgeColor(poll.state)} fontSize="sm">
                    {poll.state}
                  </Badge>
                </Box>

                {poll.description && (
                  <Text mb={4} color="gray.600">
                    {poll.description}
                  </Text>
                )}

                <Link
                  as={ReactRouterLink}
                  to={`/polls/${poll.id}/results`}
                  color={linkColor}
                  fontWeight="bold"
                >
                  View Results →
                </Link>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );
}

export default ResultsPage;
